import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { AutoComplete, Spin } from 'antd'
import { getListenerAddressSuggestions } from '../../../api/listeners/api'
import type { IAddressSuggestion, IResponseAddressSuggestions } from '../../../api/common.types'
import inputCss from '../../ui/input/index.module.scss'
import css from './index.module.scss'

type ListenerAddressInputProps = {
    value: string
    onChange: (value: string) => void
    isDisabled?: boolean
    label?: string
    loadSuggestions?: (query: string, signal?: AbortSignal) => Promise<IResponseAddressSuggestions>
    suffix?: ReactNode
    onSuggestionSelect?: (value: string) => void
}

export const ListenerAddressInput = ({
    value, onChange, isDisabled = false,
    label = 'Адрес прописки слушателя', loadSuggestions = getListenerAddressSuggestions, suffix, onSuggestionSelect,
}: ListenerAddressInputProps) => {
    const [query, setQuery] = useState<string | null>(null)
    const [suggestions, setSuggestions] = useState<IAddressSuggestion[]>([])
    const [message, setMessage] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [isOpen, setIsOpen] = useState(false)
    const [missingPostcode, setMissingPostcode] = useState(false)
    const requestRef = useRef<AbortController | null>(null)

    const stopSearch = () => {
        requestRef.current?.abort()
        setQuery(null)
        setSuggestions([])
        setMessage('')
        setIsLoading(false)
        setIsOpen(false)
    }

    const search = (text: string) => {
        requestRef.current?.abort()
        setSuggestions([])
        setMessage('')
        setMissingPostcode(false)
        const canSearch = text.trim().length >= 3 && text.trim().length <= 300 && !isDisabled
        setQuery(canSearch ? text : null)
        setIsLoading(canSearch)
        setIsOpen(canSearch)
    }

    useEffect(() => {
        if (isDisabled) {
            requestRef.current?.abort()
            setQuery(null)
            setSuggestions([])
            setIsLoading(false)
            setIsOpen(false)
            setMissingPostcode(false)
        }
    }, [isDisabled])

    useEffect(() => {
        if (query === null || query !== value || isDisabled) return
        const controller = new AbortController()
        requestRef.current = controller
        const timer = setTimeout(async () => {
            try {
                const response = await loadSuggestions(query.trim(), controller.signal)
                if (controller.signal.aborted) return
                if (response.status === 200 && response.data) {
                    setSuggestions(response.data.suggestions)
                    setMessage(response.data.error || (response.data.suggestions.length ? '' : 'Адреса не найдены.'))
                } else {
                    setMessage(response.message || 'Адресные подсказки временно недоступны.')
                }
            } catch {
                if (!controller.signal.aborted) setMessage('Адресные подсказки временно недоступны.')
            } finally {
                if (!controller.signal.aborted) setIsLoading(false)
            }
        }, 350)
        return () => {
            clearTimeout(timer)
            controller.abort()
        }
    }, [query, value, isDisabled, loadSuggestions])

    const isCurrentQuery = query !== null && query === value && !isDisabled
    const options = suggestions.length ? suggestions.map(suggestion => ({
        value: suggestion.value,
        label: <div className={css.option}>
            <span>{suggestion.value}</span>
            {!suggestion.postalCode && <span className={css.note}>Индекс не определён</span>}
        </div>,
    })) : [{
        value: '__address_search_status__',
        disabled: true,
        label: <span role="status" className={css.status}>{isLoading ? 'Поиск адреса...' : message}</span>,
    }]

    return <div>
        <div className={`${inputCss.primary} ${inputCss.md} ${css.field} ${isDisabled ? inputCss.disabled : ''}`} onClick={event => event.stopPropagation()}>
            <AutoComplete
                className={css.control}
                classNames={{ popup: { root: css.popup } }}
                variant="borderless"
                value={value}
                options={options}
                open={isOpen && isCurrentQuery}
                disabled={isDisabled}
                allowClear
                virtual={false}
                filterOption={false}
                defaultActiveFirstOption={false}
                getPopupContainer={() => document.body}
                onChange={nextValue => onChange(nextValue)}
                onSearch={search}
                onFocus={() => search(value)}
                onBlur={stopSearch}
                onOpenChange={setIsOpen}
                onClear={() => { stopSearch(); setMissingPostcode(false) }}
                onSelect={selected => {
                    const suggestion = suggestions.find(item => item.value === selected)
                    stopSearch()
                    setMissingPostcode(Boolean(suggestion && !suggestion.postalCode))
                    if (suggestion) onSuggestionSelect?.(suggestion.value)
                }}
            >
                <input className={inputCss.md} placeholder={label} aria-label={label} autoComplete="off" maxLength={300} />
            </AutoComplete>
            {suffix}
            {isLoading && isCurrentQuery && <Spin size="small" styles={{ indicator: { color: 'var(--color_txt_accent)' } }} />}
        </div>
        {missingPostcode && <div role="status" className={css.note}>Почтовый индекс не определён.</div>}
    </div>
}